class TermsAgreement extends HTMLElement {
  connectedCallback() {
    this.onDocumentClick = this.onDocumentClick.bind(this);
    // Capture phase so we get first look at the click, before any other script reacts to it.
    document.addEventListener('click', this.onDocumentClick, true);

    // Snapshot the checkout button's untouched class list now, while nothing has been
    // clicked yet, so we can restore it later even if some other script on this page (there's
    // at least one, unrelated to this theme, that reacts to the same click) mutates it.
    this.captureCleanButtonState();

    // Browsers restore form control state (including checkbox .checked) on back/forward
    // navigation independently of any app code - we set this to true right before navigating to
    // checkout on accept, so returning via back can silently restore it checked even though nothing
    // in our own state says the shopper agreed on *this* visit to the cart. Uncheck it on every
    // connect (a fresh cart-drawer render after navigating back) and pageshow (a bfcache resume of
    // this exact instance, which doesn't re-run connectedCallback) so agreement is never carried
    // over from a previous visit to the page.
    this.onPageShow = this.onPageShow.bind(this);
    window.addEventListener('pageshow', this.onPageShow);
    this.resetCheckbox();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow(event) {
    if (event.persisted) this.resetCheckbox();
  }

  resetCheckbox() {
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (checkbox) checkbox.checked = false;
  }

  captureCleanButtonState() {
    const form = document.getElementById(this.getAttribute('for'));
    const button = form && form.querySelector('button[name="checkout"]');
    this.cleanButtonClass = button ? button.className : null;
  }

  restoreButton(trigger) {
    if (this.cleanButtonClass !== null) trigger.className = this.cleanButtonClass;
    // Klip Coupons (a separately installed app, not theme code) doesn't just add a class here —
    // it inserts its own <span class="elsklip-btn-loader"> loading indicator into the button,
    // whose animation/opacity state is left visually stuck once its click handling runs but the
    // navigation it expected never happens. Remove just that injected element: not the whole
    // button's contents, which would also wipe Impact's own cached loading-spinner nodes for it.
    trigger.querySelectorAll('.elsklip-btn-loader').forEach((el) => el.remove());
  }

  onDocumentClick(event) {
    const form = document.getElementById(this.getAttribute('for'));
    if (!form) return;

    // Klip Coupons (a separately installed app, not theme code - see restoreButton()) does two
    // different things with this button that both show up as synthetic (non-trusted) clicks:
    //   1. It intercepts the shopper's real tap with its own overlay, so event.target for that
    //      real click never reaches the button at all - then ~1-2s later it re-dispatches a
    //      synthetic click on the actual button itself to let checkout proceed. This is how
    //      checkout normally happens through Klip's flow, so we must still gate it.
    //   2. On its own initiative (observed ~1-2s after returning to the page from checkout, with
    //      no shopper interaction behind it at all) it also dispatches that same kind of
    //      synthetic click. We must NOT treat this as a real checkout attempt.
    // We can't tell these apart by the event alone, so track real interactions with this form and
    // only honor a synthetic click if a real one happened here a moment before it (case 1).
    if (event.isTrusted) {
      if (form.contains(event.target)) this.lastTrustedInteraction = performance.now();
    } else if (!this.lastTrustedInteraction || performance.now() - this.lastTrustedInteraction > 5000) {
      return;
    }

    const trigger = event.target.closest('button[name="checkout"]');

    if (!trigger || !form.contains(trigger)) return;

    const checkbox = this.querySelector('input[type="checkbox"]');
    if (!checkbox || checkbox.checked) return;

    event.preventDefault();
    event.stopPropagation();

    // Other scripts on this page can also react to this same click (e.g. Klip Coupons adding a
    // loading indicator in anticipation of navigating to checkout) before we get to block it.
    // Since the navigation never happens, nothing ever clears that state for them. Restore the
    // button to how it looked before this click, once whatever else was going to run has run.
    // Runs twice: once immediately after, and once a little later in case that other script's
    // own reaction is itself delayed (observed happening asynchronously, not always same-tick).
    // (Deliberately setTimeout, not requestAnimationFrame: rAF only fires on an actual paint,
    // which some browser/tab states can suspend indefinitely.)
    setTimeout(() => this.restoreButton(trigger), 0);
    setTimeout(() => this.restoreButton(trigger), 500);

    const dialog = document.getElementById('terms-agreement-dialog');
    if (dialog) {
      dialog.pendingAgreement = this;
      dialog.pendingTrigger = trigger;
      dialog.show();
    }
  }

  accept() {
    const checkbox = this.querySelector('input[type="checkbox"]');
    if (checkbox) checkbox.checked = true;
  }
}

customElements.define('terms-agreement', TermsAgreement);

// The dialog is built on Impact's own dialog-element (see terms-agreement-dialog.liquid) rather
// than a plain custom element, so its focus trap stacks correctly with <cart-drawer>'s. But its
// built-in "click outside closes the dialog" handling (DialogElement._allowOutsideClick, part of
// the theme's own bundle) doesn't check whether that outside click was real - Klip Coupons (see
// TermsAgreement.onDocumentClick above) dispatches synthetic clicks as part of its own flow, and
// if one of those lands outside the dialog, it closes the dialog even though nothing the shopper
// did caused it. Subclassing to ignore non-trusted clicks for this purpose only changes how our
// own dialog reacts to being clicked - it doesn't touch Klip's script or behavior at all.
const DialogElementBase = customElements.get('dialog-element');

class TermsAgreementDialogElement extends DialogElementBase {
  _allowOutsideClick(event) {
    if (!event.isTrusted) return false;
    return super._allowOutsideClick(event);
  }
}

customElements.define('terms-agreement-dialog-element', TermsAgreementDialogElement);

// We just wire up the Accept/Decline buttons inside it.
function initTermsAgreementDialog() {
  const dialog = document.getElementById('terms-agreement-dialog');
  if (!dialog) return;

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      dialog.hide();
      return;
    }

    const button = event.target.closest('button[name]');
    if (!button) return;

    if (button.name === 'decline') {
      dialog.hide();
      return;
    }

    if (button.name === 'accept') {
      const agreement = dialog.pendingAgreement;
      const trigger = dialog.pendingTrigger;

      dialog.hide();
      if (agreement) agreement.accept();

      // Submit the original form directly (as this button, so its name/value posts as normal)
      // rather than re-clicking it: hiding the dialog can happen mid-transition alongside the
      // cart drawer's own DOM changes, and re-clicking the button is not reliable in that window.
      if (trigger && trigger.form) trigger.form.requestSubmit(trigger);
    }
  });
}

initTermsAgreementDialog();
